import React, { useMemo, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import { ICONS } from "../../lib/leafletIcons";

/**
 * RouteMap — reusable map component for Route Schedule module.
 *
 * Props:
 *  - customers: [{id, name, customer_code, lat, lng, selected?}]    — pins
 *  - start: {lat, lng, label?}                                       — start marker (optional)
 *  - end:   {lat, lng, label?}                                       — end marker (optional)
 *  - route: [{seq, lat, lng, customer_name, status?}]                — optimized stops
 *             when provided, renders numbered pins + a polyline in order start→...→end
 *  - onPinClick: (customer) => void                                  — list/map sync hook
 *  - height:  css height string (default 420px)
 */
export default function RouteMap({
  customers = [], start, end, route, onPinClick, height = "420px",
}) {
  const pts = [
    ...customers.filter(c => c.lat != null && c.lng != null),
    ...(start?.lat != null ? [{ lat: start.lat, lng: start.lng }] : []),
    ...(end?.lat != null ? [{ lat: end.lat, lng: end.lng }] : []),
    ...(route || []).filter(s => s.lat != null && s.lng != null),
  ];
  const center = useMemo(() => {
    if (pts.length === 0) return [9.93, 76.26]; // Kochi fallback
    const avg = pts.reduce((a, p) => [a[0] + p.lat, a[1] + p.lng], [0, 0]);
    return [avg[0] / pts.length, avg[1] / pts.length];
  }, [pts.length]); // eslint-disable-line

  const polyline = route && route.length > 0 ? [
    ...(start ? [[start.lat, start.lng]] : []),
    ...route.map(s => [s.lat, s.lng]),
    ...(end ? [[end.lat, end.lng]] : (start ? [[start.lat, start.lng]] : [])),
  ] : null;

  return (
    <div style={{ height }} data-testid="route-map" className="rounded-md overflow-hidden border border-[#E5E2D9]">
      <MapContainer center={center} zoom={customers.length > 20 ? 11 : 13} style={{ height: "100%", width: "100%" }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap'/>
        <FitBounds points={pts}/>
        {/* Unsequenced customer pins (used in Create-Plan mode) */}
        {!route && customers.map(c => (
          <Marker key={c.id} position={[c.lat, c.lng]}
                  icon={c.selected ? ICONS.selected : ICONS.customer}
                  eventHandlers={{ click: () => onPinClick?.(c) }}>
            <Popup>
              <div className="text-xs">
                <div className="font-medium">{c.name}</div>
                <div className="font-mono text-[10px] text-[#8A8780]">{c.customer_code}</div>
                {c.phone && <div className="text-[10px]">{c.phone}</div>}
              </div>
            </Popup>
          </Marker>
        ))}
        {/* Numbered route stops */}
        {route && route.map(s => (
          <Marker key={s.seq} position={[s.lat, s.lng]}
                  icon={ICONS.seq(s.seq,
                    s.status === "completed" ? "#2D5A27"
                    : s.status === "skipped" ? "#C25D38"
                    : "#3A7033")}>
            <Popup>
              <div className="text-xs">
                <b>Stop #{s.seq}</b> — {s.customer_name}<br/>
                <span className="font-mono text-[10px] text-[#8A8780]">{s.customer_code}</span>
                {s.status && <div className="text-[10px]">{s.status}</div>}
              </div>
            </Popup>
          </Marker>
        ))}
        {start && <Marker position={[start.lat, start.lng]} icon={ICONS.start}>
          <Popup>Start {start.label ? `· ${start.label}` : ""}</Popup></Marker>}
        {end && (end.lat !== start?.lat || end.lng !== start?.lng) && (
          <Marker position={[end.lat, end.lng]} icon={ICONS.end}>
            <Popup>End {end.label ? `· ${end.label}` : ""}</Popup></Marker>
        )}
        {polyline && <Polyline positions={polyline} color="#2D5A27" weight={4} opacity={0.7}/>}
      </MapContainer>
    </div>
  );
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length < 2) return;
    const bounds = points.map(p => [p.lat, p.lng]);
    try { map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 }); } catch { /* noop */ }
  }, [points.length]); // eslint-disable-line
  return null;
}
